import { MountedPortal } from '#app/components/MountedPortal/MountedPortal.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { Text } from '#app/components/Primitives/Text.js';
import { springAnimation } from '#app/utilities/animation.js';
import {
  Arrow,
  Button,
  TooltipActions,
  TooltipContainer,
  TooltipParagraph,
  TooltipReferenceContainer,
  TooltipSubtitle,
  TooltipWrapper,
} from './OnboardingTooltip.styles.js';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { useMount } from '@contra/react-hooks/useMount';
import { type Placement, type PositioningStrategy } from '@popperjs/core';
import { useTooltip } from '@react-aria/tooltip';
import { AnimatePresence } from 'framer-motion';
import { type ReactNode, useRef } from 'react';
import { usePopper } from 'react-popper';
import { type DefaultTheme, useTheme } from 'styled-components';

export type ArrowPosition =
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'inferred'
  | 'left-bottom'
  | 'left-center'
  | 'left-top'
  | 'right-bottom'
  | 'right-center'
  | 'right-top'
  | 'right-top'
  | 'top-center'
  | 'top-left'
  | 'top-right';

export type TooltipCompoundProps = {
  readonly arrowPosition?: ArrowPosition;
  readonly body: ReactNode;
  readonly children: ReactNode;
  readonly cta: string;
  readonly heading: ReactNode;
  readonly icon?: ReactNode;
  readonly isOpen: boolean;
  readonly mode?: 'dark' | 'light';
  readonly offset?: [null | number | undefined, null | number | undefined];
  readonly onClick: () => void;
  readonly placement?: Placement;
  readonly stacking?: keyof Pick<
    DefaultTheme['zIndices'],
    'modal' | 'navbar' | 'tooltip'
  >;
  readonly stepCount?: string;
  readonly strategy?: PositioningStrategy;
  readonly width?: number | string;
};

const getInferredArrowPosition = (placement?: Placement): ArrowPosition => {
  switch (placement) {
    case 'bottom':
      return 'top-center';
    case 'left':
      return 'right-center';
    case 'right':
      return 'left-center';
    case 'top':
      return 'bottom-center';
    default:
      return 'top-center';
  }
};

export const OnboardingTooltip = ({
  arrowPosition = 'bottom-right',
  body,
  children,
  cta,
  heading,
  icon,
  isOpen,
  mode = 'dark',
  offset = [8, 0],
  onClick,
  placement = 'top-end',
  stacking = 'tooltip',
  stepCount,
  strategy = 'absolute',
  width,
  ...props
}: TooltipCompoundProps) => {
  const theme = useTheme();
  const { tooltipProps } = useTooltip({
    isOpen,
    ...props,
  });
  const referenceElement = useRef<HTMLDivElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);
  const { attributes, state, styles, update } = usePopper(
    referenceElement.current,
    popperElement.current,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          enabled: true,
          name: 'hide',
        },
      ],
      placement,
      strategy,
    },
  );
  const currentTooltipPlacement = state?.placement;

  useIsomorphicLayoutEffect(() => {
    if (isOpen && update) void update();
  }, [isOpen, update]);

  useMount(() => {
    // this is needed to ensure correct positioning of the tooltip
    if (isOpen && update) void update();
  });

  const popperElementWidth = width ?? 'auto';

  return (
    <TooltipWrapper $width={popperElementWidth}>
      <TooltipReferenceContainer
        $hideOutlineOnFocus
        ref={referenceElement}
        tabIndex={0}
      >
        {children}
      </TooltipReferenceContainer>
      <MountedPortal id="onboarding-tooltip-portal">
        <div
          ref={popperElement}
          style={{
            ...styles['popper'],
            width: popperElementWidth,
            zIndex:
              stacking === 'modal'
                ? Number(theme.zIndices.modal.dialog) + 1
                : Number(theme.zIndices[stacking]) + 1,
          }}
          {...attributes['popper']}
        >
          <AnimatePresence>
            {isOpen ? (
              <animated.div
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                initial={{ opacity: 0, y: -8 }}
                transition={springAnimation.fast}
              >
                <TooltipContainer
                  {...tooltipProps}
                  $mode={mode}
                  $width={width}
                >
                  <Arrow
                    position={
                      arrowPosition === 'inferred'
                        ? getInferredArrowPosition(currentTooltipPlacement)
                        : arrowPosition
                    }
                  />
                  {icon}
                  <Text
                    my="8px"
                    textStyle="subtitleSmall"
                  >
                    {heading}
                  </Text>
                  <Text textStyle="captionRegular">{body}</Text>
                  <TooltipActions>
                    <Text textStyle="captionRegular">{stepCount}</Text>
                    <Button
                      onClick={onClick}
                      type="button"
                    >
                      <Text textStyle="captionBold">{cta}</Text>
                    </Button>
                  </TooltipActions>
                </TooltipContainer>
              </animated.div>
            ) : null}
          </AnimatePresence>
        </div>
      </MountedPortal>
    </TooltipWrapper>
  );
};

OnboardingTooltip.Paragraph = TooltipParagraph;
OnboardingTooltip.Subtitle = TooltipSubtitle;
